import React from 'react';
import { Link, Image, TimeLocation, MaskedImage, RichText, Heading } from '@/components';
import type { ImageAsset, PortableTextBlock as PortableTextBlockType } from '@sanity/types';
import { InternalLinkBuilder } from '@/utils';
import { InternalLinkInterface } from '@/types';
import { EventDates, EventExceptions } from '@/types/EventInterface';

interface ImageWithAlt {
  alt?: string;
  image: {
    asset: ImageAsset;
  };
}

interface ImageWithMask extends ImageWithAlt {
  mask?: string;
}

interface Slug {
  current?: string;
}

interface PageParent {
  slug: Slug;
  pageParent?: PageParent;
}

interface FeaturedEntry {
  _id: string;
  _type: string;
  slug?: Slug;
  pageParent?: PageParent;
  title?: string;
  description?: string;
  blurb?: PortableTextBlockType;
  carouselImage?: ImageWithMask;
  content?: PortableTextBlockType;
  featuredImage?: ImageWithAlt;
  location?: string;
  startDate?: string;
  endDate?: string;
  recommendedAges?: string;
  language: string;
  link?: {
    label: string;
    internalLink?: InternalLinkInterface;
    externalLink?: string;
  };
  seo?: {
    description?: string;
    image?: ImageWithAlt;
  };
  eventDates?: EventDates[];
  exceptions?: EventExceptions[];
}

interface Props {
  id: string | number;
  entry: FeaturedEntry;
  titleClassName?: string;
  descriptionClassName?: string;
  imageRounded?: boolean;
  showTimeLocation?: boolean;
  className?: string;
  imageContainerClass?: string;
  imageClass?: string;
  maskType?: string;
  imageLayout?: string;
  isBiggerLayout?: boolean;
  defaultSEO?: {
    description: string;
    image: ImageWithAlt;
  };
}

const FeaturedProgram: React.FC<Props> = ({
  entry,
  titleClassName,
  descriptionClassName,
  imageRounded,
  showTimeLocation,
  className,
  imageContainerClass,
  imageClass,
  maskType,
  imageLayout,
  isBiggerLayout,
  defaultSEO,
}) => {
  const getImageData = () => {
    if (entry?._type === 'customCarouselItem' && entry?.carouselImage) {
      return entry.carouselImage;
    } else if (entry?.featuredImage) {
      return entry.featuredImage;
    } else if (entry.seo?.image) {
      return entry.seo?.image;
    }
    return defaultSEO?.image;
  };

  const imageData = getImageData();
  if (!imageData) return null;

  const RenderedImage = () => {
    const getImageComponent = (imageAsset: any, alt?: string) => (
      <Image
        className={`${imageRounded ? 'rounded-2xl' : ''} ${imageClass}`}
        image={imageAsset}
        alt={alt || entry?.title}
        width={325}
      />
    );

    const getMaskedImageComponent = (imageAsset: any, alt?: string) => (
      <MaskedImage
        image={imageAsset}
        mask={maskType!}
        className={`${className ? className : ''} aspect-square w-full`}
        alt={alt || entry?.title}
        width={325}
      />
    );

    const renderConditionalImage = (imageAsset?: any, alt?: string) => {
      if (!imageAsset) return null;
      return maskType && maskType !== 'no-mask'
        ? getMaskedImageComponent(imageAsset, alt)
        : getImageComponent(imageAsset, alt);
    };

    return (
      <div className={`w-full ${imageContainerClass || ''}`}>
        {renderConditionalImage(imageData?.image, imageData?.alt)}
      </div>
    );
  };

  const RenderedTitle = ({ isLink = false }) =>
    isBiggerLayout && imageLayout == 'imageBesideContent' ? (
      <Heading
        title={entry?.title}
        isShowCurveLine={false}
        className='mb-2'
      />
    ) : (
      <h2 className={titleClassName || 'mb-1 sm:mb-1.5'}>
        <div
          className={`${isLink ? 'squiggly-link' : ''} text-2xl font-semibold leading-6 ${titleClassName}`}
        >
          {entry?.title}
        </div>
      </h2>
    );

  const description = entry?.description || entry.seo?.description || defaultSEO?.description;

  const link =
    entry._type === 'customCarouselItem' && entry.link
      ? {
          internalLink: entry.link.internalLink,
          externalLink: entry.link.externalLink,
        }
      : entry.slug?.current
        ? {
            internalLink: InternalLinkBuilder(entry),
          }
        : null;

  return (
    <div className={`h-auto ${className || ''}`}>
      <div
        className={`flex ${imageLayout == 'imageBesideContent' ? `flex-col items-center justify-center gap-5 sm:flex-row md:flex-col lg:flex-row` : 'flex-col'}`}
      >
        {link ? (
          <Link
            {...link}
            className={`squiggly-link-trigger group flex flex-col ${imageLayout == 'imageBesideContent' ? `w-full ${isBiggerLayout ? 'max-w-[350px] sm:w-1/2 md:w-full lg:w-1/2' : 'sm:w-[150px] md:w-full lg:w-[200px]'}` : ''}`}
          >
            <RenderedImage />
          </Link>
        ) : (
          <RenderedImage />
        )}

        <div className={imageLayout == 'imageBesideContent' ? `flex-1` : ``}>
          <div className={imageLayout !== 'imageBesideContent' ? 'mt-3' : ''}>
            {entry.title &&
              (link ? (
                <Link
                  {...link}
                  className='squiggly-link-trigger group flex flex-col leading-6'
                >
                  <RenderedTitle isLink />
                </Link>
              ) : (
                <RenderedTitle />
              ))}

            {description && entry._type === 'page' && (
              <p
                className={
                  imageLayout == 'imageBesideContent'
                    ? imageLayout == 'imageBesideContent' && isBiggerLayout
                      ? 'text-xl font-normal leading-7'
                      : 'text-lg font-light leading-[20px]'
                    : descriptionClassName || 'text-lg font-light'
                }
              >
                {description}
              </p>
            )}

            {entry.blurb && (
              <RichText
                value={entry.blurb}
                className={
                  imageLayout == 'imageBesideContent'
                    ? isBiggerLayout
                      ? 'text-xl font-normal leading-7'
                      : 'text-base font-light leading-[20px]'
                    : descriptionClassName || 'text-base font-light leading-[20px]'
                }
              />
            )}

            {!entry.blurb && entry.content && (
              <RichText
                as='p'
                className='text-base font-light leading-[20px]'
                value={entry.content}
              />
            )}
          </div>

          {entry?._type !== 'page' && showTimeLocation && (
            <>
              {entry?._type == 'event' &&
                imageLayout == 'imageBesideContent' &&
                entry.slug?.current && (
                  <Link
                    internalLink={InternalLinkBuilder(entry)}
                    variant='default'
                    size='sm'
                    className='mt-4'
                  >
                    view next event
                  </Link>
                )}
              <hr
                className={`border-blackTrans15 ${imageLayout == 'imageBesideContent' ? 'mb-2 mt-3 sm:mt-4' : 'my-3 sm:my-4'}`}
              />
              <TimeLocation
                type={entry?._type}
                startDate={entry?.startDate}
                endDate={entry?.endDate}
                location={entry?.location}
                recommendedAges={entry?.recommendedAges}
                eventDates={entry?.eventDates || []}
                exceptions={entry?.exceptions || []}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeaturedProgram;
